import { forwardRef, useEffect, useState, memo } from 'react'
import * as THREE from 'three'
import { extend } from '@react-three/fiber'
import { useTexture, shaderMaterial } from '@react-three/drei'
import fragmentShader from '@/shaders/videoFragment.frag'
import vertexShader from '@/shaders/vertex.vert'

type VideoProps = {
    segments?: number
    scale?: [number, number]
    color?: THREE.Color
    map?: THREE.Texture | null
    planeBounds?: [number, number]
    imageBounds?: [number, number]
    texture?: THREE.VideoTexture
    posterTexture?: THREE.Texture | null
    poster?: string
    url?: string
    position?: [number, number, number]
    uSpeed?: number
    mobile?: boolean
    toneMapped?: boolean
}

const VideoMaterialImpl = shaderMaterial(
    {
        color: new THREE.Color('white'),
        scale: [1, 1],
        imageBounds: [1, 1],
        map: null,
        uSpeed: 0,
        mobile: false
    },
    vertexShader,
    fragmentShader
)
extend({ VideoCustomMaterial: VideoMaterialImpl })

declare global {
    namespace JSX {
        interface IntrinsicElements {
            videoCustomMaterial: Pick<
                VideoProps,
                'color' | 'map' | 'scale' | 'imageBounds' | 'uSpeed' | 'mobile'
            >
        }
    }
}

const useVideoTextureOptimized = (url?: string) => {
    const [texture, setTexture] = useState<THREE.VideoTexture | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    useEffect(() => {
        if (!url) return
        const video = document.createElement('video')
        video.crossOrigin = 'anonymous'
        video.loop = true
        video.muted = true
        video.playsInline = true
        video.src = url
        const handleLoadedData = () => {
            video
                .play()
                .then(() => {
                    const videoTexture = new THREE.VideoTexture(video)
                    videoTexture.minFilter = THREE.LinearFilter
                    videoTexture.magFilter = THREE.LinearFilter
                    videoTexture.format = THREE.RGBAFormat
                    setTexture(videoTexture)
                    setIsLoaded(true)
                })
                .catch(() => {
                    // ユーザー操作が必要な場合は何もしない
                })
        }
        video.addEventListener('loadeddata', handleLoadedData)
        video.load()
        return () => {
            video.removeEventListener('loadeddata', handleLoadedData)
            if (texture) texture.dispose()
        }
    }, [url])
    return { texture, isLoaded }
}

const R3FVideoBase = forwardRef<THREE.Mesh, VideoProps>(
    (
        {
            url,
            poster,
            color,
            segments = 16,
            scale = [1, 1],
            position,
            ...props
        },
        ref
    ) => {
        const { texture, isLoaded } = useVideoTextureOptimized(url)
        const posterTexture = useTexture(poster ?? '__dummy__')
        const hasPoster = !!poster && posterTexture && poster !== '__dummy__'
        const planeBounds = [scale[0], scale[1]] as [number, number]
        const imageBounds = hasPoster
            ? ([posterTexture.image.width, posterTexture.image.height] as [
                  number,
                  number
              ])
            : ([1, 1] as [number, number])
        const materialProps = {
            color,
            scale: planeBounds,
            imageBounds,
            mobile: false,
            toneMapped: false
        } as VideoProps
        return (
            <mesh
                ref={ref}
                scale={[...scale, 1]}
                position={position}
                {...props}
            >
                <planeGeometry args={[1, 1, 1, segments]} />
                {isLoaded && texture ? (
                    <videoCustomMaterial {...materialProps} map={texture} />
                ) : hasPoster ? (
                    <videoCustomMaterial
                        {...materialProps}
                        map={posterTexture}
                    />
                ) : (
                    <meshBasicMaterial
                        color="#f0f0f0"
                        transparent
                        opacity={0.8}
                    />
                )}
            </mesh>
        )
    }
)

export const R3FVideo = memo(R3FVideoBase)
